import React, { useState } from 'react';
import BaseOverlay from '../../../components/layout/BaseOverlay.jsx';
import { useForm } from 'react-hook-form';
import { useAddTicketMutation, useGetTicketTypesQuery } from '../../../api/tickets.js';
import '../../../resources/styles/emojis.scss';
import BaseButton from '../../../components/form/BaseButton.jsx';
import TicketButton from './TicketButton.jsx';
import { useErrorToast } from '../../../hooks/useToast.jsx';

const TicketForm = () => {
    const [showOverlay, setShowOverlay] = useState(false);
    const { register, handleSubmit, reset } = useForm({
        defaultValues: {
            feedbackText: ''
        }
    });
    const [selectedTicketType, setSelectedTicketType] = useState('');
    const [selectedRating, setSelectedRating] = useState('');
    const [ticketTypeError, setTicketTypeError] = useState('');
    const [ratingError, setRatingError] = useState('');
    const [feedbackTextError, setFeedbackTextError] = useState('');

    const { data: ticketTypeData, isLoading: ticketTypeLoading } = useGetTicketTypesQuery(undefined, {
        skip: !showOverlay
    });
    const [addTicket, { isLoading: isLoadingAdding }] = useAddTicketMutation();

    const ticketTypesList = ticketTypeData?.response?.ticket_types || [];

    const emojiRating = [
        { id: 'emoji_u1f614', name: 'pensive', alt: 'Pensive Face', value: '1' },
        { id: 'emoji_u1f610', name: 'neutral', alt: 'Neutral Face', value: '2' },
        { id: 'emoji_u1f642', name: 'slightly-happy', alt: 'Slightly Smiling Face', value: '3' },
        { id: 'emoji_u1f603', name: 'smile', alt: 'Smile with big eyes', value: '4' },
        { id: 'emoji_u1f60d', name: 'heart-eyes', alt: 'Heart eyes', value: '5' }
    ];

    const showTicketForm = () => setShowOverlay(true);
    const closeOverlay = () => setShowOverlay(false);

    const handleTicketTypeSelection = (value) => {
        setSelectedTicketType(value);
        setTicketTypeError('');
    };

    const handleRatingSelection = (value) => {
        setSelectedRating(value);
        setRatingError('');
    };

    const onSubmit = async (data) => {
        if (!selectedTicketType) {
            setTicketTypeError('Please select a ticket type.');
        } else {
            setTicketTypeError('');
        }

        if (!selectedRating) {
            setRatingError('Please rate your experience.');
        } else {
            setRatingError('');
        }

        if (!data.feedbackText) {
            setFeedbackTextError('Please write your suggestion.');
        } else {
            setFeedbackTextError('');
        }

        if (!selectedTicketType || !selectedRating || !data.feedbackText) {
            return;
        }

        const formData = {
            type: selectedTicketType,
            text: data.feedbackText,
            rating: selectedRating,
            screen: window.location.href
        };

        try {
            await addTicket(formData);
            reset();
            setSelectedTicketType('');
            setSelectedRating('');
            closeOverlay();
        } catch (error) {
            console.error('Error during mutation:', error);
            useErrorToast('Ticket saving failed');
        }
    };

    return (
        <>
            <TicketButton onClick={showTicketForm} />

            <BaseOverlay show={showOverlay} className='overflow-auto h-[100dvh] rounded-none'>
                <div className='min-h-96 max-w-3xl w-full mx-auto px-8'>
                    <div className='flex justify-end py-4'>
                        <div className='text-theme1-primary font-bold cursor-pointer' onClick={closeOverlay}>
                            Close
                        </div>
                    </div>

                    <div className='text-xl font-bold text-theme1-primary2 mb-4 pb-3 border-b border-solid border-theme1-tanTransparent text-center'>
                        Help us improve
                    </div>

                    {ticketTypeLoading && (
                        <div className=' text-theme1-primary mb-10'>Loading ticket types, please wait...</div>
                    )}
                    {!ticketTypeLoading && (
                        <ul className='p-0 m-0 mb-10 list-none flex flex-wrap gap-[8px]'>
                            {ticketTypesList.map((ticketType) => (
                                <li
                                    key={ticketType.value}
                                    onClick={() => handleTicketTypeSelection(ticketType.value)}
                                    className={`border border-solid border-theme1-border1 rounded-4xl px-3 py-1 text-[12px] font-extrabold text-theme1-primary2 cursor-pointer ${
                                        selectedTicketType === ticketType.value ? 'bg-theme1-primary text-white' : ''
                                    }`}
                                >
                                    {ticketType.label}
                                </li>
                            ))}
                        </ul>
                    )}

                    {ticketTypeError && <p className='text-red-500 -mt-9 mb-9'>{ticketTypeError}</p>}

                    <div className='w-full rounded-[20px] border border-theme1-pale p-4 mb-9 bg-theme1-white'>
                        <textarea
                            {...register('feedbackText')}
                            className='w-full text-theme1-text1 border-none bg-none rounded-lg h-[100px] focus:outline-none focus:ring-0'
                            placeholder='Write your suggestions'
                            onChange={(e) => {
                                setFeedbackTextError('');
                                register('feedbackText').onChange(e);
                            }}
                        />
                    </div>
                    {feedbackTextError && <p className='text-red-500 -mt-8 mb-8'>{feedbackTextError}</p>}

                    <div className='text-base font-bold text-theme1-primary2 mb-4'>How's your experience so far?</div>
                    <ul className='flex gap-3 mb-10 justify-between'>
                        {emojiRating.map((emoji) => (
                            <li
                                key={emoji.id}
                                onClick={() => handleRatingSelection(emoji.value)}
                                className='cursor-pointer'
                            >
                                <img
                                    src={
                                        new URL(
                                            `/src/resources/images/emoji/${emoji.name}.${
                                                selectedRating === emoji.value ? 'webp' : 'svg'
                                            }`,
                                            import.meta.url
                                        ).href
                                    }
                                    width={40}
                                    height={40}
                                    alt={emoji.alt}
                                    className={`bw-emoji ${selectedRating === emoji.value ? 'selected-emoji' : ''}`}
                                />
                            </li>
                        ))}
                    </ul>
                    {ratingError && <p className='text-red-500 -mt-9 mb-9'>{ratingError}</p>}

                    <BaseButton
                        className='mt-4 btn-red-outline'
                        textValue={isLoadingAdding ? 'Saving ticket...' : 'Submit'}
                        onClick={handleSubmit(onSubmit)}
                        disabled={ticketTypeLoading || isLoadingAdding}
                    />
                </div>
            </BaseOverlay>
        </>
    );
};

export default TicketForm;
